import { PropsWithChildren, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { PageLimit } from '@/components/TablePersonalizada';
import { LogViewerModal } from '../components/LogViewerModal';

type LogViewerMode = 'table' | 'record';

interface OpenBaseOptions {
	table: string;
	label?: string | null;
	initialParams?: Partial<PageLimit>;
}

interface OpenRecordOptions extends OpenBaseOptions {
	idRegister: string;
}

interface LogViewerState extends OpenBaseOptions {
	isOpen: boolean;
	mode: LogViewerMode;
	idRegister: string | null;
}

interface LogViewerContextValue {
	state: LogViewerState;
	openTableLogs: (options: OpenBaseOptions) => void;
	openRecordLogs: (options: OpenRecordOptions) => void;
	close: () => void;
}

export const DEFAULT_LOG_VIEWER_STATE: LogViewerState = {
	isOpen: false,
	mode: 'table',
	table: '',
	idRegister: null,
	label: null,
	initialParams: undefined,
};

const LogViewerContext = createContext<LogViewerContextValue | undefined>(undefined);

export const LogViewerProvider = ({ children }: PropsWithChildren) => {
		const [state, setState] = useState<LogViewerState>(DEFAULT_LOG_VIEWER_STATE);

	const openTableLogs = useCallback(({ table, label, initialParams }: OpenBaseOptions) => {
		setState({
			isOpen: true,
			mode: 'table',
			table,
			idRegister: null,
			label,
			initialParams,
		});
	}, []);

	const openRecordLogs = useCallback(
		({ table, idRegister, label, initialParams }: OpenRecordOptions) => {
			setState({
				isOpen: true,
				mode: 'record',
				table,
				idRegister,
				label,
				initialParams,
			});
		},
		[],
	);

	const close = useCallback(() => {
		setState(prev => ({ ...prev, isOpen: false }));
	}, []);

	const value = useMemo(
		() => ({
			state,
			openTableLogs,
			openRecordLogs,
			close,
		}),
		[state, close, openTableLogs, openRecordLogs],
	);

	return (
		<LogViewerContext.Provider value={value}>
			{children}
			<LogViewerModal />
		</LogViewerContext.Provider>
	);
};

export const useLogViewerContext = (): LogViewerContextValue => {
	const ctx = useContext(LogViewerContext);
	if (!ctx) {
		throw new Error('useLogViewerContext debe usarse dentro de LogViewerProvider');
	}
	return ctx;
};

export const useOptionalLogViewerContext = () => {
	return useContext(LogViewerContext);
};

